/**
 * Página unificada de Equipo — fusiona Usuarios + Roles
 * Tabs:
 * - Usuarios: gestión de estados (activar/desactivar), búsqueda, filtros
 * - Roles: asignación de roles por nivel (unión, asociación, club)
 */

import { useState, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  InlineLoader,
  SectionBadge,
} from "@/components/ui";
import {
  Users,
  Search,
  Filter,
  MoreHorizontal,
  Check,
  X,
  Pencil,
  UserX,
  UserCheck,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Mail,
  Shield,
  Plus,
  Trash2,
  Crown,
  Briefcase,
  Church,
  Building2,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  useSystemUsers,
  useDeactivateUser,
  useActivateUser,
} from "@/hooks/useUsers";
import { useRoleAssignments, useRevokeRole } from "@/hooks/useRoles";
import { useClubs, useChurches } from "@/hooks/useOrganization";
import { type SystemUser, type RoleAssignment, type ClubType } from "@/types";
import { SYSTEM_ROLE_LABELS, CLUB_TYPE_LABELS } from "@/types";
import UserModal from "@/components/modals/UserModal";
import AssignRoleModal from "@/components/modals/AssignRoleModal";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import { Can } from "@/components/auth/Can";
import { toast } from "react-hot-toast";
import { cn, getActiveSections } from "@/lib/utils";

type Tab = "usuarios" | "roles";

export default function TeamPage() {
  const { profile } = useAuthStore();
  const { isUnionAdmin, isAssociationAdmin } = usePermissions();

  const { data: users = [], isLoading: loadingUsers } = useSystemUsers();
  const { data: assignments = [], isLoading: loadingAssignments } =
    useRoleAssignments();
  const { data: clubs = [] } = useClubs();
  const { data: churches = [] } = useChurches();

  const deactivateMutation = useDeactivateUser();
  const activateMutation = useActivateUser();
  const revokeRoleMutation = useRevokeRole();

  // Tab state
  const [activeTab, setActiveTab] = useState<Tab>("usuarios");

  // --- Users tab state ---
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive" | "pending"
  >("all");
  const [showFilters, setShowFilters] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // --- Roles tab state ---
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "union",
    "association",
    "club",
  ]);
  const [directorSearch, setDirectorSearch] = useState("");
  const [clubTypeFilter, setClubTypeFilter] = useState<string>("all");
  const [expandedChurches, setExpandedChurches] = useState<Set<string>>(
    new Set(["__all__"]),
  );

  // ============ USERS TAB LOGIC ============

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          user.email.toLowerCase().includes(query) ||
          user.displayName.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (statusFilter !== "all" && user.status !== statusFilter) return false;
      return true;
    });
  }, [users, searchQuery, statusFilter]);

  const statusStats = useMemo(() => {
    return {
      active: users.filter((u) => u.status === "active").length,
      inactive: users.filter((u) => u.status === "inactive").length,
      pending: users.filter((u) => u.status === "pending").length,
    };
  }, [users]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <Badge variant="success" className="gap-1">
            <Check className="w-3 h-3" />
            Activo
          </Badge>
        );
      case "inactive":
        return (
          <Badge variant="secondary" className="gap-1">
            <X className="w-3 h-3" />
            Inactivo
          </Badge>
        );
      case "pending":
        return (
          <Badge variant="warning" className="gap-1">
            <AlertTriangle className="w-3 h-3" />
            Pendiente
          </Badge>
        );
      default:
        return null;
    }
  };

  const handleEditUser = (user: SystemUser) => {
    setSelectedUser(user);
    setIsModalOpen(true);
    setOpenMenuId(null);
  };

  const handleToggleStatus = async (user: SystemUser) => {
    const action = user.status === "active" ? "desactivar" : "activar";
    if (
      !confirm(
        `¿${action.charAt(0).toUpperCase() + action.slice(1)} a ${user.displayName}?`,
      )
    )
      return;

    try {
      if (user.status === "active") {
        await deactivateMutation.mutateAsync({
          userId: user.id,
          updatedBy: profile?.email || "system",
        });
      } else {
        await activateMutation.mutateAsync({
          userId: user.id,
          updatedBy: profile?.email || "system",
        });
      }
      setOpenMenuId(null);
    } catch (error) {
      console.error("Error toggling user status:", error);
    }
  };

  // ============ ROLES TAB LOGIC ============

  const assignmentsByRole = useMemo(() => {
    const unionAdmins = assignments.filter(
      (a) => a.role === "admin_union" && a.status === "active",
    );
    const associationAdmins = assignments.filter(
      (a) => a.role === "admin_association" && a.status === "active",
    );
    const clubDirectors = assignments.filter(
      (a) => a.role === "club_director" && a.status === "active",
    );
    return { unionAdmins, associationAdmins, clubDirectors };
  }, [assignments]);

  const groupedDirectors = useMemo(() => {
    const clubMap = new Map(clubs.map((c) => [c.id, c]));
    const churchMap = new Map(churches.map((c) => [c.id, c.name]));

    const enriched = assignmentsByRole.clubDirectors.map((a) => {
      const club = clubMap.get(a.scopeId);
      const churchId = club?.churchId || "unknown";
      const churchName =
        club?.churchName || churchMap.get(churchId) || "Iglesia desconocida";
      return {
        ...a,
        churchName,
        churchId,
        clubType: (club?.type || "conquistadores") as ClubType,
        clubSections: club ? getActiveSections(club) : ["conquistadores"],
      };
    });

    const searchLower = directorSearch.toLowerCase();
    const searched = searchLower
      ? enriched.filter(
          (a) =>
            getUserName(a.userId).toLowerCase().includes(searchLower) ||
            getUserEmail(a.userId).toLowerCase().includes(searchLower) ||
            a.scopeName.toLowerCase().includes(searchLower) ||
            a.churchName.toLowerCase().includes(searchLower),
        )
      : enriched;

    const filtered =
      clubTypeFilter === "all"
        ? searched
        : searched.filter((a) => a.clubSections.includes(clubTypeFilter));

    const churchGroups = new Map<
      string,
      { churchName: string; directors: typeof filtered }
    >();
    for (const director of filtered) {
      const existing = churchGroups.get(director.churchId);
      if (existing) {
        existing.directors.push(director);
      } else {
        churchGroups.set(director.churchId, {
          churchName: director.churchName,
          directors: [director],
        });
      }
    }

    return Array.from(churchGroups.entries())
      .sort(([, a], [, b]) => a.churchName.localeCompare(b.churchName))
      .map(([churchId, data]) => ({
        churchId,
        churchName: data.churchName,
        directors: data.directors.sort((a, b) =>
          (CLUB_TYPE_LABELS[a.clubType] || "").localeCompare(
            CLUB_TYPE_LABELS[b.clubType] || "",
          ),
        ),
      }));
  }, [
    assignmentsByRole.clubDirectors,
    clubs,
    churches,
    directorSearch,
    clubTypeFilter,
  ]);

  const getUserName = (userId: string): string => {
    const user = users.find((u) => u.id === userId);
    return user?.displayName || user?.email || "Usuario desconocido";
  };

  const getUserEmail = (userId: string): string => {
    const user = users.find((u) => u.id === userId);
    return user?.email || "";
  };

  const handleRevokeRole = async (assignment: RoleAssignment) => {
    const roleName = SYSTEM_ROLE_LABELS[assignment.role];
    if (
      !confirm(
        `¿Revocar el rol de ${roleName} de ${getUserName(assignment.userId)}?`,
      )
    )
      return;

    try {
      await revokeRoleMutation.mutateAsync({
        assignmentId: assignment.id,
        revokedBy: profile?.email || "system",
      });
      toast.success("Rol revocado exitosamente");
    } catch (error) {
      console.error("Error revoking role:", error);
      toast.error("Error al revocar el rol");
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section)
        ? prev.filter((s) => s !== section)
        : [...prev, section],
    );
  };

  const toggleChurch = (churchId: string) => {
    setExpandedChurches((prev) => {
      const next = new Set(prev);
      if (next.has(churchId)) next.delete(churchId);
      else next.add(churchId);
      return next;
    });
  };

  const isLoading = loadingUsers || loadingAssignments;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <InlineLoader size="lg" label="Cargando equipo…" />
      </div>
    );
  }

  const RoleAssignmentRow = ({
    assignment,
  }: {
    assignment: RoleAssignment;
  }) => (
    <div className="flex items-center justify-between p-4 bg-white border-2 border-gray-100 rounded-xl hover:shadow-lg hover:border-gray-200 transition-all duration-300">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-md shadow-primary-500/25">
          <span className="text-white font-semibold">
            {getUserName(assignment.userId).charAt(0).toUpperCase()}
          </span>
        </div>
        <div>
          <p className="font-medium text-gray-900">
            {getUserName(assignment.userId)}
          </p>
          <p className="text-sm font-medium text-gray-500">
            {getUserEmail(assignment.userId)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <p className="text-sm font-medium text-gray-700">
            {assignment.scopeName}
          </p>
          <p className="text-xs text-gray-400">
            Desde{" "}
            {assignment.assignedAt
              ? format(assignment.assignedAt.toDate(), "d MMM yyyy", {
                  locale: es,
                })
              : "N/A"}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleRevokeRole(assignment)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors duration-200"
          disabled={revokeRoleMutation.isPending}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // ============ TABS CONFIG ============

  const tabs: { key: Tab; label: string; icon: typeof Users; count: number }[] =
    [
      {
        key: "usuarios",
        label: "Usuarios",
        icon: Users,
        count: users.length,
      },
      {
        key: "roles",
        label: "Roles",
        icon: Shield,
        count: assignments.filter((a) => a.status === "active").length,
      },
    ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Users className="w-5 h-5 text-white" />
            </div>
            Equipo
          </h1>
          <p className="text-gray-500 mt-1 ml-[52px]">
            Gestiona usuarios y asigna roles en tu organización
          </p>
        </div>
        {activeTab === "roles" && (
          <Can permission="organization.write">
            <Button
              onClick={() => setShowAssignModal(true)}
              className="shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-300"
            >
              <Plus className="w-5 h-5 mr-2" />
              {isAssociationAdmin && !isUnionAdmin
                ? "Asignar Director"
                : "Asignar Rol"}
            </Button>
          </Can>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                activeTab === tab.key
                  ? "bg-white text-gray-900 shadow-md"
                  : "text-gray-500 hover:text-gray-700 hover:bg-white/50",
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              <Badge
                className={cn(
                  "text-[10px] ml-0.5",
                  activeTab === tab.key
                    ? "bg-primary-100 text-primary-700"
                    : "bg-gray-200 text-gray-600",
                )}
              >
                {tab.count}
              </Badge>
            </button>
          );
        })}
      </div>

      {/* ============ TAB: USUARIOS ============ */}
      {activeTab === "usuarios" && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br from-white to-green-50/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
                    <UserCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Activos</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {statusStats.active}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br from-white to-yellow-50/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/30 group-hover:scale-110 transition-transform duration-300">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Pendientes
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {statusStats.pending}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg group hover:shadow-xl hover:-translate-y-1 transition-all duration-300 bg-gradient-to-br from-white to-gray-50/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-600 rounded-xl flex items-center justify-center shadow-lg shadow-gray-500/30 group-hover:scale-110 transition-transform duration-300">
                    <UserX className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Inactivos
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {statusStats.inactive}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Users Table Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="border-b pb-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle>Usuarios del Sistema</CardTitle>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por email o nombre..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 w-full sm:w-64 transition-all duration-300"
                    />
                  </div>
                  <Button
                    variant={showFilters ? "primary" : "secondary"}
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="gap-2"
                  >
                    <Filter className="w-4 h-4" />
                    Filtros
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${showFilters ? "rotate-180" : ""}`}
                    />
                  </Button>
                </div>
              </div>

              {showFilters && (
                <div className="mt-4 pt-4 border-t">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) =>
                      setStatusFilter(e.target.value as typeof statusFilter)
                    }
                    className="px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 w-full md:w-64 transition-all duration-300"
                  >
                    <option value="all">Todos</option>
                    <option value="active">Activos</option>
                    <option value="pending">Pendientes</option>
                    <option value="inactive">Inactivos</option>
                  </select>
                </div>
              )}
            </CardHeader>

            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Usuario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Último Acceso
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center">
                          <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                            <Users className="w-8 h-8 text-gray-500" />
                          </div>
                          <p className="text-gray-500 font-medium">
                            {searchQuery || statusFilter !== "all"
                              ? "No se encontraron usuarios con los filtros aplicados"
                              : "No hay usuarios registrados"}
                          </p>
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => (
                        <tr
                          key={user.id}
                          className="hover:bg-gray-50 transition-colors duration-200"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center shadow-md shadow-purple-500/25">
                                {user.photoUrl ? (
                                  <img
                                    src={user.photoUrl}
                                    alt={user.displayName || user.email || ""}
                                    className="w-10 h-10 rounded-full object-cover"
                                  />
                                ) : (
                                  <span className="text-lg font-bold text-white">
                                    {(user.displayName || user.email || "?")
                                      .charAt(0)
                                      .toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {user.displayName ||
                                    user.email ||
                                    "Sin nombre"}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 text-gray-600">
                              <Mail className="w-4 h-4 text-gray-400" />
                              {user.email}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {getStatusBadge(user.status)}
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-gray-500">
                              {user.lastLoginAt
                                ? format(
                                    user.lastLoginAt.toDate(),
                                    "d MMM yyyy, HH:mm",
                                    { locale: es },
                                  )
                                : "Nunca"}
                            </p>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="relative">
                              <button
                                onClick={() =>
                                  setOpenMenuId(
                                    openMenuId === user.id ? null : user.id,
                                  )
                                }
                                className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                              >
                                <MoreHorizontal className="w-5 h-5 text-gray-400" />
                              </button>

                              {openMenuId === user.id && (
                                <>
                                  <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setOpenMenuId(null)}
                                  />
                                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border-2 border-gray-100 z-20">
                                    <div className="py-1">
                                      <button
                                        onClick={() => handleEditUser(user)}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors duration-200"
                                      >
                                        <Pencil className="w-4 h-4" />
                                        Editar
                                      </button>
                                      <hr className="my-1 border-gray-100" />
                                      <button
                                        onClick={() => handleToggleStatus(user)}
                                        className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors duration-200 ${
                                          user.status === "active"
                                            ? "text-red-600 hover:bg-red-50"
                                            : "text-green-600 hover:bg-green-50"
                                        }`}
                                      >
                                        {user.status === "active" ? (
                                          <>
                                            <UserX className="w-4 h-4" />
                                            Desactivar
                                          </>
                                        ) : (
                                          <>
                                            <UserCheck className="w-4 h-4" />
                                            Activar
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {filteredUsers.length > 0 && (
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Mostrando {filteredUsers.length} de {users.length} usuarios
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <UserModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedUser(null);
            }}
            user={selectedUser}
          />
        </>
      )}

      {/* ============ TAB: ROLES ============ */}
      {activeTab === "roles" && (
        <>
          {/* Stats */}
          <div
            className={`grid grid-cols-1 gap-4 ${isUnionAdmin ? "md:grid-cols-4" : "md:grid-cols-2"}`}
          >
            {isUnionAdmin && (
              <>
                <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Admins Unión
                        </p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">
                          {assignmentsByRole.unionAdmins.length}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25 group-hover:scale-110 transition-transform duration-300">
                        <Crown className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Admins Asociación
                        </p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">
                          {assignmentsByRole.associationAdmins.length}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:scale-110 transition-transform duration-300">
                        <Briefcase className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
            <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Directores Club
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">
                      {assignmentsByRole.clubDirectors.length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/25 group-hover:scale-110 transition-transform duration-300">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Usuarios Activos
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">
                      {users.filter((u) => u.status === "active").length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-600 rounded-xl flex items-center justify-center shadow-lg shadow-gray-500/25 group-hover:scale-110 transition-transform duration-300">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Union Admins */}
          {isUnionAdmin && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <div
                className="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                onClick={() => toggleSection("union")}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Crown className="w-5 h-5 text-purple-500" />
                      Administradores de Unión
                      <Badge className="bg-purple-100 text-purple-800 ml-2">
                        {assignmentsByRole.unionAdmins.length}
                      </Badge>
                    </div>
                    {expandedSections.includes("union") ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </CardTitle>
                </CardHeader>
              </div>
              {expandedSections.includes("union") && (
                <CardContent className="space-y-3">
                  {assignmentsByRole.unionAdmins.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">
                      No hay administradores de unión asignados
                    </p>
                  ) : (
                    assignmentsByRole.unionAdmins.map((assignment) => (
                      <RoleAssignmentRow
                        key={assignment.id}
                        assignment={assignment}
                      />
                    ))
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Association Admins */}
          {isUnionAdmin && (
            <Card className="border-0 shadow-lg overflow-hidden">
              <div
                className="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                onClick={() => toggleSection("association")}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-blue-500" />
                      Administradores de Asociación
                      <Badge className="bg-blue-100 text-blue-800 ml-2">
                        {assignmentsByRole.associationAdmins.length}
                      </Badge>
                    </div>
                    {expandedSections.includes("association") ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </CardTitle>
                </CardHeader>
              </div>
              {expandedSections.includes("association") && (
                <CardContent className="space-y-3">
                  {assignmentsByRole.associationAdmins.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Building2 className="w-8 h-8 text-blue-500" />
                      </div>
                      <p className="text-gray-500 mb-4">
                        No hay administradores de asociación
                      </p>
                      <Can permission="organization.write">
                        <Button
                          variant="secondary"
                          onClick={() => setShowAssignModal(true)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Asignar Admin de Asociación
                        </Button>
                      </Can>
                    </div>
                  ) : (
                    assignmentsByRole.associationAdmins.map((assignment) => (
                      <RoleAssignmentRow
                        key={assignment.id}
                        assignment={assignment}
                      />
                    ))
                  )}
                </CardContent>
              )}
            </Card>
          )}

          {/* Club Directors */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div
              className="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              onClick={() => toggleSection("club")}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-500" />
                    Directores de Club
                    <Badge className="bg-green-100 text-green-800 ml-2">
                      {assignmentsByRole.clubDirectors.length}
                    </Badge>
                  </div>
                  {expandedSections.includes("club") ? (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </CardTitle>
              </CardHeader>
            </div>
            {expandedSections.includes("club") && (
              <CardContent className="space-y-4">
                {/* Search + type filter */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre, email, club o iglesia..."
                      value={directorSearch}
                      onChange={(e) => setDirectorSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {(
                      [
                        ["all", "Todos"],
                        ["aventureros", "Aventureros"],
                        ["conquistadores", "Conquistadores"],
                        ["guias_mayores", "Guías Mayores"],
                      ] as const
                    ).map(([value, label]) => (
                      <button
                        key={value}
                        onClick={() => setClubTypeFilter(value)}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200",
                          clubTypeFilter === value
                            ? "bg-primary-500 text-white shadow-md shadow-primary-500/25"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Grouped content */}
                {assignmentsByRole.clubDirectors.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-green-500" />
                    </div>
                    <p className="text-gray-500 mb-4">
                      No hay directores de club asignados
                    </p>
                    <Can permission="organization.write">
                      <Button
                        variant="secondary"
                        onClick={() => setShowAssignModal(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Asignar Director
                      </Button>
                    </Can>
                  </div>
                ) : groupedDirectors.length === 0 ? (
                  <div className="text-center py-6">
                    <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">
                      No se encontraron resultados para la búsqueda
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {groupedDirectors.map((church) => (
                      <div
                        key={church.churchId}
                        className="border border-gray-100 rounded-xl overflow-hidden"
                      >
                        <button
                          onClick={() => toggleChurch(church.churchId)}
                          className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                        >
                          <div className="flex items-center gap-2">
                            {expandedChurches.has(church.churchId) ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            <Church className="w-4 h-4 text-gray-500" />
                            <span className="font-medium text-gray-800 text-sm">
                              {church.churchName}
                            </span>
                            <Badge className="bg-gray-200 text-gray-600 text-xs">
                              {church.directors.length}
                            </Badge>
                          </div>
                        </button>

                        {expandedChurches.has(church.churchId) && (
                          <div className="p-3 space-y-2">
                            {church.directors.map((director) => (
                              <div
                                key={director.id}
                                className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-lg hover:shadow-md hover:border-gray-200 transition-all duration-200"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-sm">
                                    <span className="text-white font-semibold text-sm">
                                      {getUserName(director.userId)
                                        .charAt(0)
                                        .toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900 text-sm">
                                      {getUserName(director.userId)}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {director.scopeName}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="flex items-center gap-1">
                                    {director.clubSections.map((s: string) => (
                                      <SectionBadge
                                        key={s}
                                        section={s as ClubType}
                                        size="xs"
                                      />
                                    ))}
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRevokeRole(director)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors duration-200"
                                    disabled={revokeRoleMutation.isPending}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          <AssignRoleModal
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
          />
        </>
      )}
    </div>
  );
}
